/**
 * Moteur canvas de l'aura électrique de l'avatar (page d'une SKILLCARD).
 *
 * Deux canvas empilés autour de l'avatar :
 *  - `back`  (derrière l'avatar) : halo qui respire, langues de flamme, éclairs
 *    qui jaillissent du bord ;
 *  - `front` (devant l'avatar)   : arcs électriques qui rampent sur le bord.
 * L'anneau qui grésille est en SVG (voir `avatar-aura.tsx`).
 *
 * La PUISSANCE (0..100, `cardAuraScore`) module chaque couche en continu
 * (`auraParams`, pur et testé) ; la TEINTE est celle de la carte (hex).
 * Repère : le canvas fait `CANVAS_SCALE` × la taille du conteneur, l'avatar a
 * un rayon de `AVATAR_RATIO` × la taille du conteneur.
 */

/** Le canvas déborde du conteneur (les flammes montent au-dessus). */
export const CANVAS_SCALE = 1.6;
/** Rayon de l'avatar / côté du conteneur (avatar 80 px dans 160 px). */
export const AVATAR_RATIO = 0.25;

export interface Rgb {
  r: number;
  g: number;
  b: number;
}

/** `#RGB` / `#RRGGBB` → rgb. Couleur invalide → bleu électrique par défaut. */
export function hexToRgb(hex: string): Rgb {
  const m = /^#?([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(hex.trim());
  if (!m) return { r: 51, g: 170, b: 255 };
  const h = m[1]!.length === 3 ? [...m[1]!].map((c) => c + c).join('') : m[1]!;
  const n = parseInt(h, 16);
  return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 };
}

/** Mélange vers le blanc (t = 0 → couleur, 1 → blanc). */
function tint({ r, g, b }: Rgb, t: number): Rgb {
  return { r: r + (255 - r) * t, g: g + (255 - g) * t, b: b + (255 - b) * t };
}

function rgba({ r, g, b }: Rgb, a: number): string {
  return `rgba(${Math.round(r)},${Math.round(g)},${Math.round(b)},${Math.max(0, Math.min(1, a))})`;
}

export interface AuraParams {
  /** Opacité du halo (0..1). */
  halo: number;
  /** Portée du halo, en rayons d'avatar. */
  haloReach: number;
  /** Flammes émises par frame (fractionnaire, accumulé). */
  flameRate: number;
  /** Taille relative des flammes. */
  flameSize: number;
  /** Probabilité par frame d'un éclair qui jaillit. */
  boltChance: number;
  /** Probabilité par frame d'un arc qui rampe sur l'avatar. */
  arcChance: number;
  /** Déformation de l'anneau SVG (échelle du feDisplacementMap). */
  ringZap: number;
}

const ramp = (p: number, from: number) => (p <= from ? 0 : (p - from) / (1 - from));

/**
 * Réglages des couches pour une puissance 0..100. 0 → aucun effet.
 * Les couches s'allument progressivement : halo dès 0, flammes, puis éclairs
 * (> 30 %), puis arcs sur l'avatar (> 65 %).
 */
export function auraParams(power: number): AuraParams {
  const p = Number.isFinite(power) ? Math.min(100, Math.max(0, power)) / 100 : 0;
  return {
    halo: 0.6 * p ** 0.6,
    haloReach: 1.3 + 0.9 * p,
    flameRate: 5 * p ** 1.3,
    flameSize: 0.55 + 0.45 * p,
    boltChance: 0.1 * ramp(p, 0.3),
    arcChance: 0.06 * ramp(p, 0.65),
    ringZap: 7 * p,
  };
}

interface Flame {
  x: number;
  y: number;
  vx: number;
  vy: number;
  life: number;
  max: number;
  size: number;
  ph: number;
}

interface Bolt {
  pts: [number, number][];
  life: number;
  max: number;
  front: boolean;
  w: number;
}

export interface AuraEngine {
  setPower(power: number): void;
  setColor(hex: string): void;
  destroy(): void;
}

/**
 * Lance l'animation sur les deux canvas. `reduced` → une seule frame figée
 * (halo seul). Retourne `null` si le canvas 2D est indisponible (jsdom…).
 */
export function createAuraEngine(
  back: HTMLCanvasElement,
  front: HTMLCanvasElement,
  { power, color, reduced }: { power: number; color: string; reduced: boolean },
): AuraEngine | null {
  const bctx = back.getContext('2d');
  const fctx = front.getContext('2d');
  if (!bctx || !fctx) return null;

  let params = auraParams(power);
  let rgb = hexToRgb(color);
  let W = 0;
  let cx = 0;
  let cy = 0;
  let R = 0;
  let raf = 0;
  let visible = true;
  let t = 0;
  let emit = 0;
  const flames: Flame[] = [];
  const bolts: Bolt[] = [];

  // Sprite de flamme pré-rendu (refait quand la teinte change).
  const sprite = document.createElement('canvas');
  sprite.width = sprite.height = 64;
  function buildSprite() {
    const s = sprite.getContext('2d');
    if (!s) return;
    s.clearRect(0, 0, 64, 64);
    const g = s.createRadialGradient(32, 32, 0, 32, 32, 32);
    g.addColorStop(0, rgba(tint(rgb, 0.9), 1));
    g.addColorStop(0.25, rgba(tint(rgb, 0.35), 0.85));
    g.addColorStop(0.6, rgba(rgb, 0.3));
    g.addColorStop(1, rgba(rgb, 0));
    s.fillStyle = g;
    s.fillRect(0, 0, 64, 64);
  }
  buildSprite();

  function resize() {
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    W = back.clientWidth;
    for (const c of [back, front]) {
      c.width = Math.round(W * dpr);
      c.height = Math.round(W * dpr);
    }
    bctx!.setTransform(dpr, 0, 0, dpr, 0, 0);
    fctx!.setTransform(dpr, 0, 0, dpr, 0, 0);
    cx = cy = W / 2;
    R = (W / CANVAS_SCALE) * AVATAR_RATIO;
  }

  function spawnFlame() {
    // Biais vers le haut : la plupart des flammes partent des côtés et du dessus.
    const spread = Math.random() < 0.7 ? 0.55 : 1;
    const a = -Math.PI / 2 + (Math.random() - 0.5) * Math.PI * 2 * spread;
    const r = R * (0.95 + Math.random() * 0.12);
    flames.push({
      x: cx + Math.cos(a) * r,
      y: cy + Math.sin(a) * r,
      vx: Math.cos(a) * (0.3 + Math.random() * 0.6) * (R / 40),
      vy: (Math.sin(a) * 0.4 - (0.8 + Math.random() * 1.4)) * (R / 40),
      life: 0,
      max: 38 + Math.random() * 40,
      size: R * (0.22 + Math.random() * 0.3) * params.flameSize,
      ph: Math.random() * Math.PI * 2,
    });
  }

  function jag(
    x1: number,
    y1: number,
    x2: number,
    y2: number,
    disp: number,
    depth: number,
    out: [number, number][],
  ) {
    if (depth === 0) {
      out.push([x2, y2]);
      return;
    }
    const mx = (x1 + x2) / 2 + (Math.random() - 0.5) * disp;
    const my = (y1 + y2) / 2 + (Math.random() - 0.5) * disp;
    jag(x1, y1, mx, my, disp / 2, depth - 1, out);
    jag(mx, my, x2, y2, disp / 2, depth - 1, out);
  }

  function makeBolt(onAvatar: boolean) {
    const pts: [number, number][] = [];
    if (onAvatar) {
      const a0 = Math.random() * Math.PI * 2;
      const span = 0.5 + Math.random() * 0.9;
      for (let i = 0; i <= 14; i++) {
        const a = a0 + (span * i) / 14;
        const rr = R * (0.9 + Math.random() * 0.18);
        pts.push([cx + Math.cos(a) * rr, cy + Math.sin(a) * rr]);
      }
    } else {
      const a = Math.random() * Math.PI * 2;
      const r2 = R * (1.35 + Math.random() * 0.55);
      const tw = (Math.random() - 0.5) * 0.6;
      const x1 = cx + Math.cos(a) * R * 1.02;
      const y1 = cy + Math.sin(a) * R * 1.02;
      pts.push([x1, y1]);
      jag(x1, y1, cx + Math.cos(a + tw) * r2, cy + Math.sin(a + tw) * r2, R * 0.35, 5, pts);
      if (Math.random() < 0.6) {
        const k = pts[Math.floor(pts.length / 2)]!;
        const branch: [number, number][] = [k];
        const ba = a + (Math.random() - 0.5) * 1.4;
        jag(
          k[0],
          k[1],
          k[0] + Math.cos(ba) * R * 0.35,
          k[1] + Math.sin(ba) * R * 0.35,
          R * 0.15,
          3,
          branch,
        );
        bolts.push({ pts: branch, life: 0, max: 6 + Math.random() * 5, front: false, w: 0.6 });
      }
    }
    bolts.push({ pts, life: 0, max: 5 + Math.random() * 7, front: onAvatar, w: 1 });
  }

  function drawBolt(ctx: CanvasRenderingContext2D, b: Bolt) {
    const flick = Math.random() < 0.25 ? 0.35 : 1;
    const a = (1 - b.life / b.max) * flick;
    const scale = R / 40;
    ctx.beginPath();
    ctx.moveTo(b.pts[0]![0], b.pts[0]![1]);
    for (let i = 1; i < b.pts.length; i++) ctx.lineTo(b.pts[i]![0], b.pts[i]![1]);
    ctx.shadowBlur = 14 * scale;
    ctx.shadowColor = rgba(rgb, 1);
    ctx.strokeStyle = rgba(tint(rgb, 0.15), a * 0.8);
    ctx.lineWidth = 4 * b.w * scale;
    ctx.stroke();
    ctx.shadowBlur = 0;
    ctx.strokeStyle = rgba(tint(rgb, 0.9), a);
    ctx.lineWidth = 1.3 * b.w * scale;
    ctx.stroke();
  }

  function drawHalo(pulse: number) {
    if (params.halo <= 0) return;
    const g = bctx!.createRadialGradient(cx, cy, R * 0.8, cx, cy, R * params.haloReach * 1.6);
    g.addColorStop(0, rgba(tint(rgb, 0.1), params.halo * pulse));
    g.addColorStop(0.45, rgba(rgb, params.halo * 0.35 * pulse));
    g.addColorStop(1, rgba(rgb, 0));
    bctx!.fillStyle = g;
    bctx!.fillRect(0, 0, W, W);
  }

  function frame() {
    raf = requestAnimationFrame(frame);
    if (!visible || W === 0) return;
    t += 1;
    bctx!.clearRect(0, 0, W, W);
    fctx!.clearRect(0, 0, W, W);

    drawHalo(0.75 + 0.25 * Math.sin(t * 0.06) + Math.random() * 0.08);

    emit += params.flameRate;
    while (emit >= 1) {
      spawnFlame();
      emit -= 1;
    }
    bctx!.globalCompositeOperation = 'lighter';
    for (let i = flames.length - 1; i >= 0; i--) {
      const p = flames[i]!;
      p.life += 1;
      const k = p.life / p.max;
      if (k >= 1) {
        flames.splice(i, 1);
        continue;
      }
      p.vy -= 0.035 * (R / 40);
      p.vx += Math.sin(p.ph + p.life * 0.18) * 0.05 + (cx - p.x) * 0.0006;
      p.x += p.vx;
      p.y += p.vy;
      const s = p.size * (1 - k * 0.75);
      bctx!.globalAlpha = Math.sin(Math.PI * k) * 0.9;
      // Langue étirée vers le haut.
      bctx!.drawImage(sprite, p.x - s * 0.42, p.y - s * 0.9, s * 0.84, s * 1.8);
    }
    bctx!.globalAlpha = 1;
    bctx!.globalCompositeOperation = 'source-over';

    if (Math.random() < params.boltChance) makeBolt(false);
    if (Math.random() < params.arcChance) makeBolt(true);
    for (let i = bolts.length - 1; i >= 0; i--) {
      const b = bolts[i]!;
      b.life += 1;
      if (b.life > b.max) {
        bolts.splice(i, 1);
        continue;
      }
      drawBolt(b.front ? fctx! : bctx!, b);
    }
  }

  function drawStill() {
    bctx!.clearRect(0, 0, W, W);
    fctx!.clearRect(0, 0, W, W);
    drawHalo(1);
  }

  resize();
  const ro =
    typeof ResizeObserver !== 'undefined'
      ? new ResizeObserver(() => {
          resize();
          if (reduced) drawStill();
        })
      : null;
  ro?.observe(back);
  // Hors écran : on ne dessine plus (économie batterie sur mobile).
  const io =
    typeof IntersectionObserver !== 'undefined'
      ? new IntersectionObserver(([e]) => {
          visible = e?.isIntersecting ?? true;
        })
      : null;
  io?.observe(back);

  if (reduced) drawStill();
  else raf = requestAnimationFrame(frame);

  return {
    setPower(next) {
      params = auraParams(next);
      if (reduced) drawStill();
    },
    setColor(hex) {
      rgb = hexToRgb(hex);
      buildSprite();
      if (reduced) drawStill();
    },
    destroy() {
      cancelAnimationFrame(raf);
      ro?.disconnect();
      io?.disconnect();
    },
  };
}
